










































































































import {
    defineComponent,
    computed,
    watch,
    onBeforeUnmount,
} from '@vue/composition-api';
import { useVuexStore } from '@superbet-group/web.lib.vue-utilities';
import { AuthenticationStore } from '@store/modules/data/authentication/authenticationIndex';
import { SportOfferStore } from '@store/modules/ui/sportOffer/sportPage/sportPageIndex';

import { VideoType } from '@core/services/media/enums';
import { StreamState } from '@store/modules/ui/sportOffer/sportPage/enums';
import {
    SdsContentLoader,
    SdsButton,
} from '@superbet-group/web.lib.ui-components';
import VideoPlayer from '@app/modules/shared/components/VideoPlayer.vue';
import YoutubePlayer from '@app/modules/shared/components/YoutubePlayer.vue';
import TwitchPlayer from '@app/modules/shared/components/TwitchPlayer.vue';
import PerformPlayer from '@app/modules/shared/components/PerformPlayer.vue';
import BetGeniusPlayer from '@app/modules/shared/components/BetGeniusPlayer.vue';
import BeterPlayer from '@app/modules/shared/components/BeterPlayer.vue';

export default defineComponent({
    name: 'VideoContainer',
    emits: [
        'videoPinClick',
        'videoCloseClick',
        'videoUnavailable',
        'requestLogin',
        'resumePlayback',
        'startPlaying',
        'toggleSound',
    ],
    inheritAttrs: false,
    props: {
        streamUrl: {
            type: String,
            default: '',
        },
        videoId: {
            type: String,
            required: false,
            default: '',
        },
        streamId: {
            type: String,
            required: false,
            default: '',
        },
        sourceId: {
            type: String,
            required: false,
            default: '',
        },
        uuid: {
            type: String,
            required: false,
            default: '',
        },
        token: {
            type: String,
            required: false,
            default: '',
        },
        videoType: {
            type: String,
            default: 'video/mp4',
        },
        state: {
            type: String,
            required: false,
            default: StreamState.NotAvailable,
        },
        isPinned: {
            type: Boolean,
            required: true,
        },
        position: {
            type: [
                Number,
                String,
            ],
            default: 1,
        },
        isDetached: {
            type: Boolean,
            default: false,
        },
        isSignedIn: {
            type: Boolean,
            default: true,
        },
        isSoundOn: {
            type: Boolean,
            default: true,
        },
    },
    setup(props, { emit }) {
        const authenticationModule = useVuexStore<AuthenticationStore>('data/authentication');
        const user = authenticationModule.getter('user');

        const sportOfferUIStore = useVuexStore<SportOfferStore>('ui/sportOffer');
        const expandedEventData = sportOfferUIStore.getter('expandedEventData');
        const selectedStreamSection = sportOfferUIStore.getter('selectedStreamSection');
        const clearDockedStream = sportOfferUIStore.action('clearDockedStream');
        const prepareStream = sportOfferUIStore.action('prepareStream');

        const isLoading = computed(() => userCanSeeVideo.value && !props.isDetached && props.state === StreamState.Loading);

        const userCanSeeVideo = computed(() => {
            if (isYoutube.value || isTwitch.value) {
                return true;
            }
            return props.isSignedIn;
        });

        const isYoutube = computed(() => VideoType.youtube === props.videoType);
        const isTwitch = computed(() => VideoType.twitch === props.videoType);
        const isBetGenius = computed(() => VideoType.betGenius === props.videoType);
        const isPerform = computed(() => VideoType.perform === props.videoType);
        const isBeter = computed(() => VideoType.beter === props.videoType);

        const isUnavailable = computed(() => userCanSeeVideo.value && !props.isDetached && props.state === StreamState.NotAvailable);
        const isPlaying = computed(() => userCanSeeVideo.value && !props.isDetached && props.state === StreamState.Playing);
        const isClosed = computed(() => userCanSeeVideo.value && !props.isDetached && props.state === StreamState.Closed);
        const isNotYetStarted = computed(() => userCanSeeVideo.value && !props.isDetached && props.state === StreamState.NotStarted);

        const username = computed(() => {
            if (!user.value) {
                return null;
            }
            return user.value.username;
        });

        const containerClass = computed(() => {
            const classNames = ['video-player-container'];
            if (props.isDetached) {
                classNames.push('video-player-container-detached');
            } else if (isUnavailable.value || !userCanSeeVideo.value) {
                classNames.push('video-player-container-unavaliable');
            } else if (props.isPinned) {
                classNames.push('video-player-container-docked');
            } else {
                classNames.push('video-player-container-floating');
            }
            return classNames;
        });

        function unpin() {
            emit('videoPinClick');
        }

        function close() {
            emit('videoCloseClick');
        }

        function setUnavailable() {
            emit('videoUnavailable');
        }

        onBeforeUnmount(() => {
            clearDockedStream();
        });

        watch(user, (newValue) => {
            if (!newValue) {
                if (props.videoType === VideoType.youtube || props.videoType === VideoType.twitch) {
                    return;
                }
                clearDockedStream();
            }

            if (newValue) {
                prepareStream({ match: expandedEventData.value, streamSection: selectedStreamSection.value });
            }
        });

        return {
            isLoading,
            userCanSeeVideo,
            isYoutube,
            isTwitch,
            isBetGenius,
            isPerform,
            isBeter,
            isUnavailable,
            isPlaying,
            isClosed,
            isNotYetStarted,
            username,
            containerClass,
            unpin,
            close,
            setUnavailable,
        };
    },

    components: {
        SdsContentLoader,
        SdsButton,
        YoutubePlayer,
        VideoPlayer,
        TwitchPlayer,
        PerformPlayer,
        BetGeniusPlayer,
        BeterPlayer,
    },
});
